import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import type { ConditionSearchHit, RatingWithEffectiveDebits } from "../types";
import { effectiveRatingDebits } from "../calc";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { notify } from "../store/uiStore";
import { reportError } from "../services/eventBus";
import { trackEvent } from "../telemetry/appInsights";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { TagPill } from "../components/Pills";
import { EmptyState, ErrorState, Skeleton } from "../components/ScreenStates";
import { ConditionSearch } from "../components/condition/ConditionSearch";
import { NumberInput, TextInput } from "../components/fields";
import { inputClass } from "../components/fields/fieldStyles";

function modificationLabel(rating: RatingWithEffectiveDebits): string | null {
  if (!rating.modification) return null;
  return rating.modification.overrideDebits !== null ? `Override ${rating.modification.overrideDebits}` : `${rating.modification.multiplier}x`;
}

interface StepsProps {
  condition: ConditionSearchHit | null;
  rating: RatingWithEffectiveDebits | null;
  onResetCondition: () => void;
  onResetRating: () => void;
}

/** Breadcrumb-style progress: Condition › Rating › Modification, earlier steps clickable. */
function Steps({ condition, rating, onResetCondition, onResetRating }: StepsProps) {
  const crumb = "inline-flex min-h-[32px] items-center rounded-full border px-3 text-xs font-medium";
  return (
    <nav aria-label="Modification steps" className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
      {condition ? (
        <button type="button" onClick={onResetCondition} className={`${crumb} cursor-pointer border-brand-green bg-brand-green-10 text-brand-green-dark hover:bg-brand-green-05`}>
          <Check size={12} className="mr-1" aria-hidden="true" /> {condition.name}
        </button>
      ) : (
        <span className={`${crumb} border-brand-navy text-brand-navy`}>1. Choose a condition</span>
      )}
      <ChevronRight size={14} aria-hidden="true" />
      {rating ? (
        <button type="button" onClick={onResetRating} className={`${crumb} cursor-pointer border-brand-green bg-brand-green-10 text-brand-green-dark hover:bg-brand-green-05`}>
          <Check size={12} className="mr-1" aria-hidden="true" /> {rating.ratingText}
        </button>
      ) : (
        <span className={`${crumb} ${condition ? "border-brand-navy text-brand-navy" : "border-gray-200 text-gray-400"}`}>2. Choose a rating</span>
      )}
      <ChevronRight size={14} aria-hidden="true" />
      <span className={`${crumb} ${rating ? "border-brand-navy text-brand-navy" : "border-gray-200 text-gray-400"}`}>3. Set the modification</span>
    </nav>
  );
}

interface ModificationFormProps {
  condition: ConditionSearchHit;
  rating: RatingWithEffectiveDebits;
  onSaved: () => void;
}

/** Multiplier or Override (one or the other) with required notes; effective debits preview live. */
function ModificationForm({ condition, rating, onSaved }: ModificationFormProps) {
  const [multiplier, setMultiplier] = useState<number | null>(rating.modification?.multiplier ?? null);
  const [overrideDebits, setOverrideDebits] = useState<number | null>(rating.modification?.overrideDebits ?? null);
  const [notes, setNotes] = useState(rating.modification?.notes ?? "");
  const [notesError, setNotesError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const hasDraft = multiplier !== null || overrideDebits !== null;
  const isDirty = multiplier !== (rating.modification?.multiplier ?? null) || overrideDebits !== (rating.modification?.overrideDebits ?? null) || notes !== (rating.modification?.notes ?? "");
  const liveEffective = effectiveRatingDebits(
    { id: rating.ratingId, guidelineRatingNumber: null, conditionId: rating.conditionId, ratingText: rating.ratingText, time: rating.time, code: null, baseDebits: rating.baseDebits, notes: null, isMedication: rating.isMedication, isCustom: rating.isCustom, introducedReleaseName: null, isActive: true },
    hasDraft ? { id: "draft", ratingId: rating.ratingId, multiplier, overrideDebits, notes, authorUserId: "", createdUtc: "", isActive: true } : null,
  );

  const handleSave = async () => {
    if (notes.trim() === "") {
      setNotesError("Notes are required on any debit change");
      return;
    }
    setNotesError(null);
    setIsSaving(true);
    try {
      const updated = await getApi().saveDebitModification(rating.ratingId, { multiplier, overrideDebits, notes: notes.trim() });
      trackEvent("DebitModificationSaved", { ratingId: String(rating.ratingId), multiplier: String(multiplier ?? ""), override: String(overrideDebits ?? "") });
      notify(hasDraft ? `${updated.ratingText} now rates ${updated.effectiveDebits} debits. Skynet sees the change immediately.` : `Modification removed from ${updated.ratingText}; it rates ${updated.effectiveDebits} debits again.`, "success");
      onSaved();
    } catch (error) {
      reportError(error, "ModifyConditionPage.save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card title={`Modify ${rating.ratingText}`} actions={<span className="text-xs text-gray-500">{condition.name}</span>}>
      <div className="space-y-4">
        <dl className="grid grid-cols-3 gap-4 rounded-lg bg-gray-50 p-3 text-sm">
          <div>
            <dt className="text-xs text-gray-500">Base debits</dt>
            <dd className="font-medium tabular-nums text-gray-800">{rating.baseDebits}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Current</dt>
            <dd className="font-medium text-gray-800">{modificationLabel(rating) ?? "No modification"}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Effective after save</dt>
            <dd className={`text-lg font-semibold tabular-nums ${liveEffective !== rating.baseDebits ? "text-brand-green-dark" : "text-gray-800"}`} aria-live="polite">
              {liveEffective}
            </dd>
          </div>
        </dl>
        <div className="grid gap-4 md:grid-cols-2">
          <NumberInput
            id="modify-multiplier"
            label="Multiplier"
            value={multiplier}
            onChange={(value) => {
              setMultiplier(value);
              if (value !== null) setOverrideDebits(null);
            }}
            readOnly={overrideDebits !== null}
            allowDecimal
            step={0.1}
            min={0}
            placeholder="e.g. 2"
            hint="Scales the base debits for every future assignment."
            isOverridden={multiplier !== null}
          />
          <NumberInput
            id="modify-override"
            label="Override"
            value={overrideDebits}
            onChange={(value) => {
              setOverrideDebits(value);
              if (value !== null) setMultiplier(null);
            }}
            readOnly={multiplier !== null}
            min={0}
            placeholder="e.g. 99999"
            hint="Replaces the base debits outright. One of Multiplier or Override, not both."
            isOverridden={overrideDebits !== null}
          />
        </div>
        <TextInput id="modify-notes" label="Notes" value={notes} onChange={setNotes} error={notesError} required hint="Why this change; shown in the Report of Modifications." />
        <div className="flex justify-end">
          <Button onClick={() => void handleSave()} disabled={!isDirty} isLoading={isSaving}>
            {hasDraft ? "Save modification" : "Remove modification"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Modify Existing Condition: condition, then rating, then a multiplier or override with notes. */
export function ModifyConditionPage() {
  usePageSetup("Modify Existing Condition", null);
  const navigate = useNavigate();
  const [condition, setCondition] = useState<ConditionSearchHit | null>(null);
  const [ratingId, setRatingId] = useState<number | null>(null);
  const [ratingFilter, setRatingFilter] = useState("");
  const ratings = useAsyncResource(async () => (condition ? getApi().getConditionRatings(condition.conditionId) : null), `${condition?.conditionId ?? "none"}`, "ModifyConditionPage.ratings");
  const rating = ratings.data?.find((entry) => entry.ratingId === ratingId) ?? null;
  const filteredRatings = useMemo(() => {
    const query = ratingFilter.trim().toLowerCase();
    return (ratings.data ?? []).filter((entry) => query === "" || entry.ratingText.toLowerCase().includes(query) || (entry.time ?? "").toLowerCase().includes(query));
  }, [ratings.data, ratingFilter]);

  const backToList = useCallback(() => navigate("/uw/actuarial/conditions"), [navigate]);
  const shortcuts = useMemo(() => ({ cancel: backToList }), [backToList]);
  useShortcuts(shortcuts);

  const resetCondition = () => {
    setCondition(null);
    setRatingId(null);
    setRatingFilter("");
  };

  return (
    <>
      <PageHeader
        title="Modify Existing Condition"
        subtitle="Change what a guideline or custom rating is worth: filter down to the rating, then set a multiplier or an override with notes."
        actions={
          <Button variant="secondary" onClick={backToList}>
            Back to customizations
          </Button>
        }
      />
      <div className="space-y-5">
        <Steps condition={condition} rating={rating} onResetCondition={resetCondition} onResetRating={() => setRatingId(null)} />
        {!condition && <ConditionSearch onPick={setCondition} />}
        {condition && !rating && (
          <Card title={`Ratings of ${condition.name}`} actions={<span className="text-xs text-gray-500">{ratings.data ? `${filteredRatings.length} of ${ratings.data.length}` : ""}</span>}>
            <div className="relative mb-3">
              <label htmlFor="rating-filter" className="sr-only">
                Filter ratings
              </label>
              <input id="rating-filter" type="search" value={ratingFilter} onChange={(event) => setRatingFilter(event.currentTarget.value)} placeholder="Filter ratings by text or timing" autoComplete="off" className={inputClass()} />
            </div>
            {ratings.state.kind === "error" && <ErrorState message={ratings.state.message} onRetry={ratings.reload} />}
            {ratings.state.kind === "loading" && !ratings.data && (
              <div className="space-y-2" role="status" aria-label="Loading ratings">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )}
            {ratings.data && ratings.data.length === 0 && <EmptyState message="This condition has no active ratings to modify." />}
            {ratings.data && ratings.data.length > 0 && filteredRatings.length === 0 && <EmptyState message="No ratings match that filter." />}
            {filteredRatings.length > 0 && (
              <ul className="divide-y divide-gray-100">
                {filteredRatings.map((entry) => (
                  <li key={entry.ratingId}>
                    <button type="button" onClick={() => setRatingId(entry.ratingId)} className="flex min-h-[44px] w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-green-50">
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5 font-medium text-gray-800">
                          {entry.ratingText}
                          {entry.isMedication && <TagPill label="Rx" />}
                          {entry.isCustom && <TagPill label="Custom" tone="green" />}
                          {modificationLabel(entry) && <TagPill label={modificationLabel(entry) ?? ""} tone="warning" />}
                        </span>
                        <span className="block text-xs text-gray-500">{entry.time ?? "No timing"}</span>
                      </span>
                      <span className="text-right text-sm tabular-nums">
                        <span className="block text-gray-500">Base {entry.baseDebits}</span>
                        <span className={`block font-semibold ${entry.effectiveDebits !== entry.baseDebits ? "text-brand-green-dark" : "text-gray-800"}`}>Effective {entry.effectiveDebits}</span>
                      </span>
                      <ChevronRight size={16} className="shrink-0 text-gray-400" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
        {condition && rating && <ModificationForm key={rating.ratingId} condition={condition} rating={rating} onSaved={backToList} />}
      </div>
    </>
  );
}
